/**
 * Stacked daily columns rendered in pure CSS (matches the dashboard's
 * existing timeline bars — no chart library on this page).
 */

import styles from './FeatureAdoptionPanel.module.css';
import { formatDay } from './formatters';

export interface StackSeries<T> {
  key: keyof T & string;
  label: string;
  /** CSS module class carrying the colour. */
  className: string;
}

interface StackChartProps<T extends { day: string }> {
  days: T[];
  series: StackSeries<T>[];
  emptyText: string;
  emptyClassName: string;
}

function valueOf<T>(row: T, key: keyof T & string): number {
  const v = row[key];
  return typeof v === 'number' ? v : 0;
}

export function StackChart<T extends { day: string }>({ days, series, emptyText, emptyClassName }: StackChartProps<T>) {
  const totals = days.map((d) => series.reduce((sum, s) => sum + valueOf(d, s.key), 0));
  const max = Math.max(...totals, 0);
  if (max === 0) {
    return <div className={emptyClassName}>{emptyText}</div>;
  }
  return (
    <div>
      <div className={styles.stackChart} role="img" aria-label="Daily stacked columns">
        {days.map((d, i) => {
          const tooltip = `${formatDay(d.day)}: ${series
            .map((s) => `${valueOf(d, s.key)} ${s.label}`)
            .join(', ')}`;
          return (
            <div key={d.day} className={styles.stackCol} data-tooltip={tooltip} title={tooltip}>
              {series.map((s) => {
                const v = valueOf(d, s.key);
                return (
                  <div
                    key={s.key}
                    className={`${styles.stackSeg} ${s.className}`}
                    style={{ height: `${(v / max) * 100}%` }}
                  />
                );
              })}
              {totals[i] === 0 && <div className={`${styles.stackSeg} ${styles.cMuted}`} style={{ height: 2 }} />}
            </div>
          );
        })}
      </div>
      <div className={styles.legend}>
        {series.map((s) => (
          <span key={s.key} className={styles.legendItem}>
            <span className={`${styles.swatch} ${s.className}`} />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}
